import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
/**
 * E-learning (LMS) — domeinmodel, geport van MoveLogic zonder course
 * snapshots (SUPPOHRT-content mag live wijzigen; examens worden altijd tegen
 * de actuele vragenpool nagekeken). Zie docs/plans/elearning-lms-bouwplan.md.
 *
 * Firestore-paden, alles onder tenants/_platform/ (shared corpus, één
 * intended runtime tenant):
 *   elearning_courses/{courseId}
 *   elearning_courses/{courseId}/modules/{moduleId}
 *   elearning_courses/{courseId}/modules/{moduleId}/lessons/{lessonId}
 *   elearning_courses/{courseId}/modules/{moduleId}/questions/{questionId}
 *   elearning_progress/{userId}_{courseId}
 *   elearning_exam_attempts/{attemptId}
 */
// ─── Curated enums (adding a value is a code change, conform CLAUDE.md) ───────
/**
 * Doelgroep-gate per cursus. Afgedwongen in de route-middleware, niet alleen
 * in queries (hard rule 1). Mapt op de bestaande Entra-gates:
 *   iedereen     → elke ingelogde rol (ook extern/RHOGO) — authenticatie
 *   intern       → suppohrt_admin + suppohrt_medewerker — isInternalRole()
 *   hoofdkantoor → Kantoorpersoneel-groep — isHeadOffice()
 */
export const CourseAudience = z.enum(['iedereen', 'intern', 'hoofdkantoor']);
export const CourseStatus = z.enum(['draft', 'active', 'archived']);
/**
 * Vraagcategorie. `oefen` mag directe feedback (isCorrect + explanation) naar
 * de browser; `examen`-vragen worden server-side nagekeken en verlaten de
 * backend NOOIT met isCorrect/explanation.
 */
export const QuestionCategory = z.enum(['oefen', 'examen']);
/**
 * Voortgangsstatus per cursist × cursus.
 *   bezig       → nog niet alle lessen af
 *   examenklaar → alle live lessen af, examen mag gestart
 *   geslaagd    → examen gehaald (score >= passPercentage)
 *   gezakt      → examen niet gehaald (herkansbaar tot maxRetakes)
 */
export const ProgressStatus = z.enum(['bezig', 'examenklaar', 'geslaagd', 'gezakt']);
// ─── Antwoordopties ──────────────────────────────────────────────────────────
/**
 * Antwoordoptie zoals opgeslagen in Firestore. `isCorrect` is server-side
 * geheim voor examenvragen; de exam-samenstel-response verwijdert dit veld.
 */
export const AnswerOption = z.object({
    text: z.string().min(1).max(2000),
    isCorrect: z.boolean(),
    sortOrder: z.number().int().min(0),
});
// ─── Course ──────────────────────────────────────────────────────────────────
export const CourseSchema = z.object({
    id: DocId,
    name: z.string().min(1).max(200),
    description: z.string().max(5000).default(''),
    status: CourseStatus.default('draft'),
    audience: CourseAudience.default('iedereen'),
    // Docent/spreker ("onder leiding van" op het certificaat); leeg = regel weg.
    instructor: z.string().max(200).default(''),
    // Certificaat na afronden aan/uit per cursus. Oude documenten missen het
    // veld: behandel ontbreken als aan.
    certificateEnabled: z.boolean().default(true),
    // Exameninstellingen per cursus. Defaults uit MoveLogic (besluit 2026-07-06).
    passPercentage: z.number().int().min(0).max(100).default(60),
    maxRetakes: z.number().int().min(0).max(50).default(3),
    cooldownMinutes: z.number().int().min(0).max(10080).default(15),
    sortOrder: z.number().int().min(0).default(0),
    createdAt: IsoTimestamp,
    updatedAt: IsoTimestamp.optional(),
    createdBy: DocId,
    // Laatste gebruiker die de cursus muteerde (audit-lichtgewicht, conform
    // de kennisbank_ring1_documents-registry).
    lastActor: DocId.optional(),
});
export const CreateCourseInput = z.object({
    name: z.string().min(1).max(200),
    description: z.string().max(5000).optional(),
    status: CourseStatus.optional(),
    audience: CourseAudience.optional(),
    instructor: z.string().max(200).optional(),
    certificateEnabled: z.boolean().optional(),
    passPercentage: z.number().int().min(0).max(100).optional(),
    maxRetakes: z.number().int().min(0).max(50).optional(),
    cooldownMinutes: z.number().int().min(0).max(10080).optional(),
});
// Every field optional — PATCH semantics. At least one is expected but an
// empty patch is harmless (no-op update touching only updatedAt/lastActor).
export const UpdateCourseInput = CreateCourseInput.partial();
// ─── Module ──────────────────────────────────────────────────────────────────
export const ModuleSchema = z.object({
    id: DocId,
    courseId: DocId,
    name: z.string().min(1).max(200),
    sortOrder: z.number().int().min(0).default(0),
    // Hoeveel examenvragen dit moduul aan een examen levert:
    //   examRequiredCount — verplichte vragen (isRequired=true), op sortOrder
    //   examRandomCount   — willekeurige overige examenvragen
    examRequiredCount: z.number().int().min(0).max(200).default(0),
    examRandomCount: z.number().int().min(0).max(200).default(0),
    createdAt: IsoTimestamp,
});
export const CreateModuleInput = z.object({
    name: z.string().min(1).max(200),
    examRequiredCount: z.number().int().min(0).max(200).optional(),
    examRandomCount: z.number().int().min(0).max(200).optional(),
});
export const UpdateModuleInput = CreateModuleInput.partial();
// ─── Lesson ──────────────────────────────────────────────────────────────────
/**
 * GCS-objectpad van een zelfgehoste lesvideo in ELEARNING_VIDEO_BUCKET,
 * `videos/{courseId}/{moduleId}/{uuid}.{mp4|webm}`. De backend munt het pad
 * bij het aanvragen van de upload-URL; het strakke patroon voorkomt dat een
 * client een willekeurig objectpad laat ondertekenen.
 */
export const VideoPath = z
    .string()
    .regex(/^videos\/[a-z0-9_-]+\/[a-z0-9_-]+\/[a-f0-9-]+\.(mp4|webm)$/i);
export const LessonSchema = z.object({
    id: DocId,
    moduleId: DocId,
    name: z.string().min(1).max(200),
    // HTML uit de rich-text editor (frontend/src/components/RichTextEditor.tsx).
    // Gefilterd bij opslag (sanitizeBody({ html: ['content'] }) op de
    // beheerroutes) én bij het renderen, want documenten van vóór die filter
    // staan nog ongefilterd in Firestore. Altijd HTML, ook als het niet met een
    // blok-tag begint: lezers moeten de `html`-vlag op RichText zetten.
    content: z.string().max(200000).default(''),
    // Zelfgehoste lesvideo (optioneel); null = geen video.
    videoPath: VideoPath.nullable().default(null),
    sortOrder: z.number().int().min(0).default(0),
    isLive: z.boolean().default(true),
    createdAt: IsoTimestamp,
});
export const CreateLessonInput = z.object({
    name: z.string().min(1).max(200),
    content: z.string().max(200000).optional(),
    videoPath: VideoPath.nullable().optional(),
    isLive: z.boolean().optional(),
});
export const UpdateLessonInput = CreateLessonInput.partial();
// ─── Webinar-opname ──────────────────────────────────────────────────────────
/**
 * Terugkijkbare webinar-opname op de Scholing-pagina (besluit 8 okt 2026, Jos).
 * Los van cursussen: één bestand + titel, geen modules/lessen. Zelfde bucket en
 * signed-URL-patroon als lesvideo's; het pad wordt server-side gemunt.
 */
export const WebinarVideoPath = z.string().regex(/^webinars\/[a-f0-9-]+\.(mp4|webm)$/i);
export const WebinarRecordingSchema = z.object({
    id: DocId,
    title: z.string().min(1).max(200),
    /** Datum waarop het webinar plaatsvond (YYYY-MM-DD), optioneel. */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .default(null),
    videoPath: WebinarVideoPath,
    createdAt: IsoTimestamp,
});
export const CreateWebinarRecordingInput = z.object({
    title: z.string().trim().min(1).max(200),
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    videoPath: WebinarVideoPath,
});
// ─── Question ────────────────────────────────────────────────────────────────
// Tiendes seconden: een hele seconde te laat toont het antwoord al in beeld.
const VideoSeconds = z.number().min(0).max(86400);
export const QuestionSchema = z.object({
    id: DocId,
    moduleId: DocId,
    title: z.string().min(1).max(300),
    questionText: z.string().max(5000).default(''),
    answerOptions: z.array(AnswerOption).min(2).max(10),
    explanation: z.string().max(5000).default(''),
    points: z.number().int().min(1).max(100).default(1),
    category: QuestionCategory,
    // `verplichte` examenvraag — bepaalt of de vraag in de examRequiredCount-pool
    // van het moduul valt. Niet in het oorspronkelijke bouwplan-datamodel, maar
    // nodig om de examRequiredCount/examRandomCount-splitsing te implementeren
    // (MoveLogic heeft is_required); zie rapport-afwijkingen.
    isRequired: z.boolean().default(false),
    // Vraagmoment in de lesvideo van dit moduul (seconden vanaf start). Gezet =
    // de video pauzeert hier en de vraag verschijnt erover; zo'n vraag telt niet
    // mee als losse oefenvraag. Alleen voor categorie `oefen` (directe feedback).
    videoSeconds: VideoSeconds.nullable().default(null),
    sortOrder: z.number().int().min(0).default(0),
    isLive: z.boolean().default(true),
    createdAt: IsoTimestamp,
});
// Answer-option payload for create/update — no server-managed fields.
const AnswerOptionInput = z.object({
    text: z.string().min(1).max(2000),
    isCorrect: z.boolean(),
    sortOrder: z.number().int().min(0),
});
export const CreateQuestionInput = z.object({
    title: z.string().min(1).max(300),
    questionText: z.string().max(5000).optional(),
    answerOptions: z.array(AnswerOptionInput).min(2).max(10),
    explanation: z.string().max(5000).optional(),
    points: z.number().int().min(1).max(100).optional(),
    category: QuestionCategory,
    isRequired: z.boolean().optional(),
    videoSeconds: VideoSeconds.nullable().optional(),
    isLive: z.boolean().optional(),
});
export const UpdateQuestionInput = CreateQuestionInput.partial();
// ─── Reorder ─────────────────────────────────────────────────────────────────
/**
 * Herorden-payload: de volledige, gewenste volgorde van ids. De service
 * schrijft `sortOrder = index` in één batch.
 */
export const ReorderInput = z.object({
    ids: z.array(DocId).min(1).max(500),
});
// ─── Progress ────────────────────────────────────────────────────────────────
export const ProgressSchema = z.object({
    userId: DocId,
    courseId: DocId,
    completedLessonIds: z.array(DocId).default([]),
    status: ProgressStatus.default('bezig'),
    examAttemptsCount: z.number().int().min(0).default(0),
    bestExamScore: z.number().int().min(0).max(100).nullable().default(null),
    // Gekozen antwoord (optie-index) per videovraag: het bewijs dat de cursist
    // alle vraagmomenten in de video is langsgekomen. Fout antwoord telt ook.
    videoAnswers: z.record(DocId, z.number().int().min(0)).optional(),
    startedAt: IsoTimestamp,
    completedAt: IsoTimestamp.optional(),
    updatedAt: IsoTimestamp,
});
/** Les afronden; een les met videovragen vereist een antwoord op elke vraag. */
// Express 5 laat req.body undefined bij een POST zonder body; default({}) vangt dat.
export const CompleteLessonInput = z
    .object({
    videoAnswers: z.record(DocId, z.number().int().min(0).max(9)).optional(),
})
    .default({});
/** Eén antwoord op een videovraag, opgeslagen zodra de cursist klikt. */
export const VideoAnswerInput = z.object({
    questionId: DocId,
    optionIndex: z.number().int().min(0).max(9),
});
// ─── Exam attempts ───────────────────────────────────────────────────────────
/**
 * Eén ingeleverd antwoord: welke opties (per index in de teruggegeven,
 * op sortOrder gesorteerde optielijst) de cursist aankruiste.
 */
export const ExamAnswer = z.object({
    questionId: DocId,
    selectedOptionIndexes: z.array(z.number().int().min(0)).max(10),
});
export const ScorePerModule = z.object({
    moduleId: DocId,
    moduleName: z.string(),
    correct: z.number().int().min(0),
    total: z.number().int().min(0),
    percentage: z.number().int().min(0).max(100),
});
export const ExamAttemptSchema = z.object({
    id: DocId,
    userId: DocId,
    courseId: DocId,
    attemptNumber: z.number().int().min(1),
    // De samengestelde vragen-ids (per moduul), vastgelegd bij het starten zodat
    // inleveren tegen exact dezelfde selectie nakijkt.
    questionIds: z.array(DocId),
    answers: z.array(ExamAnswer).nullable().default(null),
    scorePercentage: z.number().int().min(0).max(100).nullable().default(null),
    scorePerModule: z.array(ScorePerModule).nullable().default(null),
    passed: z.boolean().nullable().default(null),
    startedAt: IsoTimestamp,
    submittedAt: IsoTimestamp.nullable().default(null),
});
/**
 * Inlever-payload van de cursist.
 */
export const SubmitExamInput = z.object({
    answers: z.array(ExamAnswer).max(500),
});
// ─── Client-safe projecties (NOOIT isCorrect/explanation naar de cursist) ────
/**
 * Examenvraag zoals de cursist hem krijgt: antwoordopties met alleen tekst +
 * sortOrder, GEEN isCorrect en GEEN explanation. Dit is het antwoord-lekkage-
 * hek: de exam-samenstel-response bevat uitsluitend deze vorm.
 */
export const ExamQuestionClient = z.object({
    id: DocId,
    moduleId: DocId,
    moduleName: z.string(),
    title: z.string(),
    questionText: z.string(),
    points: z.number().int(),
    answerOptions: z.array(z.object({
        text: z.string(),
        sortOrder: z.number().int(),
    })),
});
//# sourceMappingURL=elearning.js.map